import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@apollo/client';
import { onAuthStateChanged, User } from 'firebase/auth';

import { FIND_USER_BY_ID } from '../../app/graphql/queries';
import { useAppDispatch, useAppSelector } from '../../app/store/hooks';
import { getFbAuth } from '../../firebase/config';
import { checkOnlineStatus } from '../../utils/os.utils';
import { setIsAuthLoading, setUserHasuraID } from './auth.slice';
import { authInit, logout } from './auth.thunk';

export const UpAuthContext = createContext({
  upAuthLoading: false,
  isAuthenticated: false,
  logout: async () => {},
});

export const UpAuth = ({ children }: { children: ReactNode }): JSX.Element => {
  const [fbUser, setFbUser] = useState<User | null>(null);
  const isAuthLoading = useAppSelector((state) => state.auth.isAuthLoading);
  const isAuthenticatedData = useAppSelector(
    (state) => state.auth.fireBaseAuth && state.auth.dataAuth
  );
  const dispatch = useAppDispatch();
  // TODO: It does not make sense to use upUser when the user data is stored in redux
  // This might cause a descrapancy and a bug, use the upUser data from the reducer
  const { loading: userDataLoading, data: { users: [upUser] = [] } = {} } =
    useQuery(FIND_USER_BY_ID, {
      variables: {
        _eq: fbUser?.uid,
      },
      skip: !isAuthenticatedData || !fbUser?.uid,
      onCompleted: (data) => {
        const userData = data.users[0];
        if (userData) {
          dispatch(setUserHasuraID({ userHasuraId: userData.id, userData }));
        }
      },
      onError: async (err) => {
        console.error(err);
        const onlineStatus = await checkOnlineStatus();
        if (!onlineStatus) {
          window.location.href = '/not-found ';
        }
        dispatch(setIsAuthLoading(false));
      },
    });

  useEffect(() => {
    const authStateCallback = (fireBaseUser: any) => {
      if (fireBaseUser) {
        setFbUser(fireBaseUser);
        window.webkit?.messageHandlers?.orgHandler?.postMessage('upliance');
        if (window?.NativeCookie?.setCookie) {
          window.NativeCookie.setCookie(
            'userDomain=upliance; Path=/; SameSite=Lax'
          );
        }
      }
    };
    dispatch(authInit());
    onAuthStateChanged(getFbAuth, authStateCallback);
  }, [dispatch]);

  const upAuthContextValue = useMemo(
    () => ({
      upAuthLoading: isAuthLoading || userDataLoading,
      isAuthenticated: !!isAuthenticatedData && !!upUser,
      logout: () => dispatch(logout()),
    }),
    [isAuthLoading, userDataLoading, isAuthenticatedData, upUser, dispatch]
  );

  // console.log({ step: 'UpAuth', upAuthContextValue, isAuthLoading, loading, upUser, isAuthenticatedData })

  return (
    <UpAuthContext.Provider value={upAuthContextValue}>
      {children}
    </UpAuthContext.Provider>
  );
};

// TODO: THis does not need to be a custom hook, just export the context and use it like regular context.
export const useUpAuth = () => useContext(UpAuthContext);
